import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, TrendingUp, Heart, Clock, Trophy, Dumbbell, Wine, PlusCircle, Play, MapPin, Navigation } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { resolveMoods, resolveSections } from "@/lib/layout";
import { PlaceCard } from "@/components/PlaceCard";
import { EventCard } from "@/components/EventCard";
import { VideoEmbed, HeroVideo, VideoCard, SocialEmbed, RawEmbed } from "@/components/VideoEmbed";
import { MapView } from "@/components/MapView";
import { detectPlatform, isVertical } from "@/lib/video";
import { isOpenNow } from "@/lib/hours";

const HERO = "https://images.unsplash.com/photo-1761054190336-69c4f243e687?crop=entropy&cs=srgb&fm=jpg&q=85";

const DEFAULT_HERO = {
  hero_image: HERO,
  hero_eyebrow: "What's good right now",
  hero_title: "Discover the heartbeat of Aiken & the CSRA",
  hero_subtitle: "Restaurants, live music, horses, gardens & hidden gems — always fresh, always local.",
};

function isToday(iso) {
  const d = new Date(iso), n = new Date();
  return d.toDateString() === n.toDateString();
}
function isThisWeekend(iso) {
  const d = new Date(iso), day = d.getDay();
  const n = new Date();
  const diff = (d - n) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 7 && (day === 5 || day === 6 || day === 0);
}

export default function Home() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [ads, setAds] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [sports, setSports] = useState([]);
  const [fitness, setFitness] = useState([]);
  const [openNow, setOpenNow] = useState(false);
  const [customData, setCustomData] = useState({});
  const [videos, setVideos] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [nearbyStatus, setNearbyStatus] = useState("idle");
  const [userLoc, setUserLoc] = useState(null);

  useEffect(() => {
    api.get("/places", { params: { featured: true } }).then((r) => setFeatured(r.data));
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/ads").then((r) => setAds(r.data));
    api.get("/places/top-rated").then((r) => setTopRated(r.data));
    api.get("/places", { params: { category: "Sports" } }).then((r) => setSports(r.data));
    api.get("/places", { params: { category: "Fitness" } }).then((r) => setFitness(r.data));
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  const customRows = settings?.custom_rows || [];
  useEffect(() => {
    customRows.forEach((r) => {
      const params = r.source_type === "tag" ? { tag: r.value } : { category: r.value };
      api.get("/places", { params }).then((res) => setCustomData((d) => ({ ...d, [r.id]: res.data }))).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(customRows.map((r) => `${r.id}:${r.source_type}:${r.value}`))]);

  const hero = { ...DEFAULT_HERO, ...(settings || {}) };
  const moods = resolveMoods(settings);
  const sections = resolveSections(settings).filter((s) => s.visible !== false);

  const today = events.filter((e) => isToday(e.start_datetime));
  const weekend = events.filter((e) => isThisWeekend(e.start_datetime) && !isToday(e.start_datetime));
  const nativeAd = ads.find((a) => a.type === "native_ad");
  const order = settings?.featured_order || [];
  const orderedFeatured = [...featured].sort((a, b) => {
    const ia = order.indexOf(a.id), ib = order.indexOf(b.id);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  const showFeatured = openNow ? orderedFeatured.filter(isOpenNow) : orderedFeatured;
  const showTopRated = openNow ? topRated.filter(isOpenNow) : topRated;
  const showSports = openNow ? sports.filter(isOpenNow) : sports;
  const showFitness = openNow ? fitness.filter(isOpenNow) : fitness;
  const featuredVideos = videos.filter((v) => v.is_featured).slice(0, 2);

  const goMood = (mood) => {
    const params = new URLSearchParams(mood.query).toString();
    navigate(`/explore?${params}&mood=${mood.key}`);
  };

  const findNearby = () => {
    if (!navigator.geolocation) { setNearbyStatus("unsupported"); return; }
    setNearbyStatus("loading");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          setUserLoc({ lat: latitude, lng: longitude });
          const { data } = await api.get("/places/nearby", { params: { lat: latitude, lng: longitude, radius_km: 60, limit: 12 } });
          setNearby(data);
          setNearbyStatus(data.length ? "done" : "empty");
        } catch { setNearbyStatus("empty"); }
      },
      () => setNearbyStatus("denied"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const RENDERERS = {
    mood: () => (
      <section className="mt-8" key="mood">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="font-serif text-2xl font-semibold">What's the mood?</h2>
          <button onClick={() => setOpenNow((v) => !v)} data-testid="home-open-now"
            aria-pressed={openNow}
            className={`flex flex-shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${openNow ? "border-green-500 bg-green-500 text-white" : "border-border bg-card text-foreground hover:border-primary"}`}>
            <Clock size={14} /> Open now
          </button>
        </div>
        <div className="no-scrollbar mt-4 flex gap-3 overflow-x-auto pb-2">
          {moods.filter((m) => m.visible !== false).map((m) => (
            <button
              key={m.key}
              onClick={() => goMood(m)}
              data-testid={`mood-${m.key}`}
              className="flex-shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-all hover:-translate-y-0.5 hover:border-primary hover:text-primary"
            >
              {m.label}
            </button>
          ))}
        </div>
      </section>
    ),
    near_me: () => (
      <section className="mt-10" data-testid="home-near-me" key="near_me">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold"><Navigation size={20} className="text-primary" /> Places near you</h2>
          {nearbyStatus !== "done" && (
            <button onClick={findNearby} data-testid="near-me-btn" disabled={nearbyStatus === "loading"}
              className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              <MapPin size={14} /> {nearbyStatus === "loading" ? "Finding you…" : "Use my location"}
            </button>
          )}
        </div>
        {nearbyStatus === "idle" && <p className="text-sm text-muted-foreground">Tap “Use my location” to discover great spots right around you.</p>}
        {nearbyStatus === "denied" && <p className="text-sm text-muted-foreground" data-testid="near-me-denied">Location access was blocked. Enable it in your browser to see nearby places.</p>}
        {nearbyStatus === "unsupported" && <p className="text-sm text-muted-foreground">Your browser doesn’t support location. Try Explore instead.</p>}
        {nearbyStatus === "empty" && <p className="text-sm text-muted-foreground" data-testid="near-me-empty">No listings found within ~37 miles. Explore the full map instead.</p>}
        {nearbyStatus === "done" && (
          <>
            <div className="card-row" data-testid="near-me-list">
              {nearby.map((p, i) => (
                <div key={p.id} className="relative">
                  <span className="absolute right-3 top-3 z-10 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid={`near-me-distance-${p.id}`}>{p.distance_mi} mi</span>
                  <PlaceCard place={p} index={i} />
                </div>
              ))}
            </div>
            {userLoc && nearby.some((p) => p.lat && p.lng) && (
              <div className="mt-5" data-testid="near-me-map">
                <MapView places={nearby} userLoc={userLoc} height={320} />
              </div>
            )}
          </>
        )}
      </section>
    ),
    featured_video: () => (
      featuredVideos.length ? (
        <section className="mt-10" data-testid="home-featured-video" key="featured_video">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold"><Play size={20} className="text-primary" /> Featured video{featuredVideos.length > 1 ? "s" : ""}</h2>
            <Link to="/videos" className="text-sm font-medium text-primary hover:underline">Watch more</Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {featuredVideos.map((fv) => {
              const days = fv.featured_expires_at ? Math.ceil((new Date(fv.featured_expires_at) - new Date()) / 86400000) : 0;
              return (
                <div key={fv.id} className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`home-featured-video-${fv.id}`}>
                  <VideoCard video={fv} />
                  <div className="p-3">
                    <p className="truncate text-sm font-semibold">{fv.title}</p>
                    {days > 0 && <p className="mt-1 flex items-center gap-1 text-xs font-medium text-primary" data-testid="featured-countdown"><Clock size={11} /> {days} day{days === 1 ? "" : "s"} left</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null
    ),
    videos: () => (
      videos.length ? (
        <section className="mt-10" data-testid="home-videos-row" key="videos">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold"><Play size={20} className="text-primary" /> Watch the CSRA</h2>
            <Link to="/videos" className="text-sm font-medium text-primary hover:underline">See all</Link>
          </div>
          <div className="no-scrollbar flex gap-4 overflow-x-auto pb-2">
            {videos.slice(0, 8).map((v) => {
              const p = v.platform || detectPlatform(v.url);
              const social = !!v.embed_html || p === "tiktok" || p === "instagram" || p === "facebook";
              if (social) {
                return (
                  <Link key={v.id} to="/videos" data-testid={`home-video-${v.id}`}
                    className="group relative flex aspect-[9/16] w-56 shrink-0 flex-col justify-end overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/80 to-secondary/70 p-3 text-white">
                    {v.thumbnail && <img src={v.thumbnail} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />}
                    <span className="absolute right-2 top-2 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-semibold uppercase">{p}</span>
                    <span className="relative z-10 flex items-center gap-1.5 text-sm font-semibold"><Play size={16} className="fill-current" /> {v.title}</span>
                  </Link>
                );
              }
              return (
                <div key={v.id} className="w-56 shrink-0 overflow-hidden rounded-2xl border border-border bg-card" data-testid={`home-video-${v.id}`}>
                  <VideoCard video={v} />
                  <p className="truncate p-2.5 text-sm font-semibold">{v.title}</p>
                </div>
              );
            })}
          </div>
        </section>
      ) : null
    ),
    featured: () => (
      <section className="mt-10" key="featured">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold">
            <Sparkles className="text-accent" size={22} /> Featured & Sponsored
          </h2>
          <Link to="/explore" className="text-sm font-medium text-primary hover:underline">See all</Link>
        </div>
        <div className="grid auto-rows-[240px] grid-cols-1 gap-5 md:grid-cols-3">
          {showFeatured.slice(0, 5).map((p, i) => (
            <PlaceCard key={p.id} place={p} index={i} featured={i === 0} fill />
          ))}
        </div>
        {nativeAd && (
          <Link to="/sponsor" data-testid="native-ad-banner"
             className="card-hover mt-10 flex flex-col items-center gap-4 overflow-hidden rounded-2xl border border-accent/40 bg-gradient-to-r from-accent/15 to-primary/10 p-6 md:flex-row">
            <img src={nativeAd.image} alt="" loading="lazy" decoding="async" className="h-24 w-full rounded-xl object-cover md:w-40" />
            <div className="flex-1 text-center md:text-left">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Advertisement</span>
              <h3 className="font-serif text-xl font-bold">{nativeAd.title}</h3>
              <p className="text-sm text-muted-foreground">{nativeAd.description}</p>
            </div>
            <span className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Feature your business</span>
          </Link>
        )}
      </section>
    ),
    today: () => today.length > 0 && (
      <section className="mt-10" key="today">
        <h2 className="mb-4 font-serif text-2xl font-semibold">Happening Today</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {today.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
        </div>
      </section>
    ),
    locals_love: () => topRated.length > 0 && (
      <section className="mt-10" key="locals_love">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold">
            <Heart className="fill-primary text-primary" size={20} /> Locals Love
          </h2>
          <span className="hidden text-sm text-muted-foreground sm:inline">Top-rated across the CSRA</span>
        </div>
        <div className="card-row" data-testid="locals-love-grid">
          {showTopRated.slice(0, 6).map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
        </div>
      </section>
    ),
    sports: () => sports.length > 0 && (
      <section className="mt-10" data-testid="home-sports-row" key="sports">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold">
            <Trophy className="text-[#1F7A8C]" size={20} /> Get in the game
          </h2>
          <Link to="/explore?category=Sports" className="text-sm font-medium text-primary hover:underline">All sports</Link>
        </div>
        <div className="card-row">
          {showSports.slice(0, 6).map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
        </div>
      </section>
    ),
    fitness: () => fitness.length > 0 && (
      <section className="mt-10" data-testid="home-fitness-row" key="fitness">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold">
            <Dumbbell className="text-[#C2410C]" size={20} /> Move your body
          </h2>
          <Link to="/explore?category=Fitness" className="text-sm font-medium text-primary hover:underline">All fitness</Link>
        </div>
        <div className="card-row">
          {showFitness.slice(0, 6).map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
        </div>
      </section>
    ),
    submit_cta: () => (
      <Link to="/submit" data-testid="home-submit-cta" key="submit_cta"
        className="card-hover mt-10 flex flex-col items-center gap-4 overflow-hidden rounded-2xl border border-secondary/30 bg-gradient-to-r from-secondary/12 to-primary/10 p-6 md:flex-row">
        <div className="flex-1 text-center md:text-left">
          <span className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest text-secondary md:justify-start">
            <PlusCircle size={13} /> Know a great local spot?
          </span>
          <h3 className="font-serif text-xl font-bold">Add your business or event to CSRAGo</h3>
          <p className="text-sm text-muted-foreground">Free to submit — our team reviews every listing before it goes live.</p>
        </div>
        <span className="flex items-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-sm font-semibold text-secondary-foreground">
          Submit a listing <ArrowRight size={15} />
        </span>
      </Link>
    ),
    wine_beer: () => (
      <Link to="/trail/wine-beer" data-testid="home-wine-beer-trail" key="wine_beer"
        className="card-hover mt-10 flex flex-col items-center gap-4 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/12 to-accent/12 p-6 md:flex-row">
        <img src="https://images.unsplash.com/photo-1645518044896-a3cbba9afb57?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Wine & Beer Trail" loading="lazy" decoding="async" className="h-24 w-full rounded-xl object-cover md:w-40" />
        <div className="flex-1 text-center md:text-left">
          <span className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary md:justify-start">
            <Wine size={13} /> Curated date night
          </span>
          <h3 className="font-serif text-xl font-bold">The CSRA Wine & Beer Trail</h3>
          <p className="text-sm text-muted-foreground">A hand-picked crawl through the region's best wine bars & taprooms.</p>
        </div>
        <span className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
          Start the trail <ArrowRight size={15} />
        </span>
      </Link>
    ),
    weekend: () => (
      <section className="mb-16 mt-10" key="weekend">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif text-2xl font-semibold">This Weekend</h2>
          <Link to="/events" className="text-sm font-medium text-primary hover:underline">Full calendar</Link>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {(weekend.length ? weekend : events).slice(0, 6).map((e, i) => (
            <EventCard key={e.id} event={e} index={i} />
          ))}
        </div>
      </section>
    ),
  };

  return (
    <div className="px-4 md:px-8">
      {/* Hero */}
      <section className="relative mt-4 overflow-hidden rounded-3xl">
        {hero.hero_video ? (
          <div className="h-[62vh] max-h-[560px] w-full overflow-hidden bg-black">
            <HeroVideo url={(hero.hero_video || "").replace(/^https?:\/\/[^/]+\/api\/files\//, "/api/files/")} className="h-full w-full object-cover" />
          </div>
        ) : (
          <img src={(hero.hero_image || "").replace(/^https?:\/\/[^/]+\/api\/files\//, "/api/files/")} alt="CSRA" fetchPriority="high" decoding="async" className="h-[62vh] max-h-[560px] w-full object-cover" />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground">
              <TrendingUp size={13} /> {hero.hero_eyebrow}
            </span>
            <h1 className="max-w-3xl font-serif text-4xl font-bold leading-none tracking-tight text-white text-shadow-soft sm:text-5xl lg:text-6xl">
              {hero.hero_title}
            </h1>
            <p className="mt-4 max-w-xl text-base text-white/85 text-shadow-soft md:text-lg">
              {hero.hero_subtitle}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/explore" data-testid="hero-explore-btn"
                className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
                Start exploring <ArrowRight size={16} />
              </Link>
              <Link to="/itinerary" data-testid="hero-plan-btn"
                className="flex items-center gap-2 rounded-full bg-white/95 px-6 py-3 text-sm font-semibold text-foreground transition-transform hover:scale-105">
                Plan my day
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Configurable sections */}
      {sections.map((s) => {
        let node = null;
        if (s.key.startsWith("custom_")) {
          const row = customRows.find((r) => `custom_${r.id}` === s.key);
          if (row) {
            const items = customData[row.id] || [];
            if (items.length) {
              const shown = openNow ? items.filter(isOpenNow) : items;
              node = (
                <section className="mt-10" data-testid={`home-custom-row-${row.id}`}>
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-serif text-2xl font-semibold">{row.title}</h2>
                    <Link
                      to={`/explore?${row.source_type === "tag" ? "tag" : "category"}=${encodeURIComponent(row.value)}`}
                      className="text-sm font-medium text-primary hover:underline">See all</Link>
                  </div>
                  <div className="card-row">
                    {shown.slice(0, 6).map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
                  </div>
                </section>
              );
            }
          }
        } else {
          node = RENDERERS[s.key] && RENDERERS[s.key]();
        }
        if (!node) return null;
        return <div id={`section-${s.key}`} key={s.key} style={{ scrollMarginTop: "80px" }}>{node}</div>;
      })}
    </div>
  );
}
